/**
 * Lobby. One screen: the code others scan, who is sitting down, and Start.
 * Seats are a strip of four chips rather than four full-width rows — at this
 * point nobody needs a paragraph about each empty chair.
 */

import type { PlayerView, Seat } from '@manille/core';

import { clear, el, initials } from './dom.js';
import { logoElement } from './logo.js';
import { qrSvg } from './qr.js';
import { toast } from './toast.js';

export interface LobbyActions {
  start(): void;
  setBot(seat: Seat, bot: boolean): void;
  setName(name: string): void;
  leave(): void;
}

export interface LobbyScreen {
  readonly root: HTMLElement;
  update(view: PlayerView): void;
  destroy(): void;
}

export function mountLobby(actions: LobbyActions): LobbyScreen {
  const root = el('div', 'lobby');

  const close = el('button', 'leave-button', '✕');
  close.setAttribute('aria-label', 'Tafel verlaten');
  close.addEventListener('click', () => actions.leave());

  const title = logoElement();

  const qr = el('button', 'qr');
  qr.setAttribute('aria-label', 'Deel deze tafel');

  const codeValue = el('div', 'lobby__code', '·····');
  const codeHint = el('div', 'lobby__hint', 'Scan de code, of tik erop om de link te delen');

  const seatStrip = el('div', 'seat-strip');

  const nameField = el('input', 'field field--name') as HTMLInputElement;
  nameField.placeholder = 'Je naam';
  nameField.maxLength = 16;
  nameField.setAttribute('autocomplete', 'nickname');

  const startButton = el('button', 'btn btn--primary btn--start', 'Start');
  const hint = el('div', 'lobby__hint');
  const waiting = el('div', 'lobby__hint', 'Wachten tot de host start…');

  // The close button floats in the corner; everything else is one centred
  // stack, so nothing is stranded at the top of a tall screen.
  root.append(close, title, qr, codeValue, codeHint, seatStrip, nameField, startButton, hint, waiting);

  let joinUrl = '';

  nameField.addEventListener('change', () => {
    const value = nameField.value.trim();
    if (value) actions.setName(value);
  });

  qr.addEventListener('click', () => void share());
  startButton.addEventListener('click', () => actions.start());

  async function share(): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Manille', text: 'Kom aan mijn tafel', url: joinUrl });
        return;
      } catch {
        return; // the user dismissed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast('Link gekopieerd.');
    } catch {
      toast(joinUrl);
    }
  }

  function update(view: PlayerView): void {
    joinUrl = `${location.origin}/?g=${view.gameId}`;
    codeValue.textContent = view.gameId;
    if (qr.dataset['for'] !== joinUrl) {
      qr.dataset['for'] = joinUrl;
      qr.innerHTML = qrSvg(joinUrl);
    }
    if (document.activeElement !== nameField) {
      nameField.value = view.seats.find((s) => s.seat === view.you.seat)?.name ?? '';
    }

    clear(seatStrip);
    for (const seat of view.seats) {
      const chip = el('button', 'seat-chip');
      chip.dataset['kind'] = seat.kind;
      chip.dataset['team'] = seat.team === view.you.team ? 'us' : 'them';

      const avatar = el('div', 'avatar', seat.kind === 'empty' ? '+' : initials(seat.name || '?'));
      avatar.dataset['team'] = seat.team === view.you.team ? 'us' : 'them';
      avatar.classList.toggle('avatar--bot', seat.kind === 'bot');

      const label =
        seat.seat === view.you.seat
          ? 'jij'
          : seat.kind === 'bot'
            ? 'bot'
            : seat.kind === 'human'
              ? seat.name || 'speler'
              : 'leeg';
      chip.append(avatar, el('span', 'seat-chip__label', label));

      // The host fills or clears a seat by tapping it; everyone else just looks.
      if (view.you.isHost && seat.kind !== 'human') {
        chip.title = seat.kind === 'bot' ? 'Bot weghalen' : 'Bot hier zetten';
        chip.addEventListener('click', () => actions.setBot(seat.seat, seat.kind !== 'bot'));
      } else {
        chip.disabled = true;
      }
      seatStrip.append(chip);
    }

    const empties = view.seats.filter((s) => s.kind === 'empty').length;
    hint.textContent =
      empties > 0
        ? `${empties} lege plaats${empties === 1 ? '' : 'en'} ${empties === 1 ? 'wordt' : 'worden'} door bots gespeeld`
        : 'Iedereen zit klaar';

    startButton.hidden = !view.you.isHost;
    hint.hidden = !view.you.isHost;
    waiting.hidden = view.you.isHost;
  }

  return { root, update, destroy: () => root.remove() };
}
